
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Coins, DollarSign } from "lucide-react";
import { SavingsGoal } from "./GoalCard";
import { toast } from "@/hooks/use-toast";

interface GoalContributeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: SavingsGoal | null;
  onContribute: (goalId: string, amount: number) => void;
}

export const GoalContributeDialog = ({
  open,
  onOpenChange,
  goal,
  onContribute,
}: GoalContributeDialogProps) => {
  // Form state
  const [amount, setAmount] = useState("");

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!goal) return;
    
    const contributionAmount = parseFloat(amount);
    
    // Validate amount
    if (isNaN(contributionAmount) || contributionAmount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid contribution amount.",
        variant: "destructive"
      });
      return;
    }
    
    // Check if this would exceed the goal
    const remainingAmount = goal.target - goal.currentAmount;
    if (contributionAmount > remainingAmount) {
      toast({
        title: "Amount exceeds goal",
        description: `The maximum contribution amount is $${remainingAmount.toFixed(2)}.`,
        variant: "destructive"
      });
      return;
    }
    
    // Process contribution
    onContribute(goal.id, contributionAmount);
    
    // Reset form and close dialog
    setAmount("");
    onOpenChange(false);
  };

  // Handle amount change with validation
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or valid numbers
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Coins className="w-5 h-5 text-primary" />
            Contribute to {goal?.name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Goal information */}
          {goal && (
            <div className="rounded-lg bg-secondary/10 p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Current amount:</span>
                <span className="text-sm">${goal.currentAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Target amount:</span>
                <span className="text-sm">${goal.target.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Remaining:</span>
                <span className="text-sm">${(goal.target - goal.currentAmount).toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Contribution amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Contribution Amount ($)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="text"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0.00"
                className="pl-10"
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" className="w-full">
              <Coins className="mr-2 h-4 w-4" />
              Contribute
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
